import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, filter, map, shareReplay, tap } from 'rxjs/operators';
import { countryOfBusiness, zipIsRequired } from '../_data/country-states';
import { BusinessSetupService } from './business-setup.service';
import { GlobalService } from './global.service';
import { PlanDataService } from './plan-data.service';
import { ProductsService } from './products.service';
import { StripeCardSavedEvent } from './stripe-card.service';

export interface BusinessSetupProgress {
  channelsComplete: boolean;
  businessComplete: boolean;
  domainComplete: boolean;
  productsComplete: boolean;
  shippingComplete: boolean;
  stripeComplete: boolean;
  cardComplete: boolean;
  completedCount: number;
  totalCount: number;
  /** Whether Domain & store template / Shipping apply at all — an in-store-only merchant has
   *  neither, dropping totalCount from 6 to 4. Exposed so other UI (e.g. the welcome modal) can
   *  hide those two steps instead of just miscounting against a hardcoded list of 6. */
  sellsOnline: boolean;
  /** Whether the Billing card step applies at all — a Free plan is never billed, so there's no
   *  card to add. Exposed (mirrors sellsOnline) so other UI (the welcome modal) can hide the
   *  card step instead of relying on cardComplete's "!cardStepNeeded" shortcut, which auto-passes
   *  the step for Free plans regardless of whether a card is actually on file. */
  cardStepNeeded: boolean;
  /** Whether the Connect Stripe step applies at all — false when the backend flagged the merchant
   *  as unable to use Stripe (ccPaymentEnable === false). stripeComplete is true in that case only
   *  because the step doesn't apply, so UI that lists steps (the welcome modal) must hide it
   *  rather than show it ticked. */
  stripeStepNeeded: boolean;
}

@Injectable({ providedIn: 'root' })
export class BusinessSetupProgressService {
  private readonly salesChannelsStorageKey = 'business-setup-sales-channels-saved';
  private readonly domainTemplateStorageKey = 'business-setup-domain-template-saved';
  private readonly customOnlineStoreRequestedStorageKey = 'business-setup-custom-online-store-requested';
  private readonly shippingConfigurationStorageKey = 'business-setup-shipping-configuration-saved';
  private cachedProgress$: Observable<BusinessSetupProgress> | null = null;
  private readonly progressSubject = new BehaviorSubject<BusinessSetupProgress | null>(null);
  /** True while a forced refetch (invalidate()) is actually in flight — lets UI (e.g. the
   *  topbar setup badge) show a skeleton instead of a stale count during that window. Does not
   *  cover the very first, uncached load, which already renders nothing until progress$ emits. */
  private readonly refreshingSubject = new BehaviorSubject<boolean>(false);
  readonly refreshing$: Observable<boolean> = this.refreshingSubject.asObservable();

  /** Continuous stream that re-emits whenever invalidate() triggers a refetch — subscribe to this for real-time UI (e.g. the topbar badge) instead of the one-shot getProgress(). */
  readonly progress$: Observable<BusinessSetupProgress> = this.progressSubject.pipe(
    filter((progress): progress is BusinessSetupProgress => progress !== null)
  );

  constructor(
    private readonly businessSetupService: BusinessSetupService,
    private readonly productsService: ProductsService,
    private readonly planDataService: PlanDataService
  ) {
    // Any successful zone save or update completes the Shipping step.
    this.businessSetupService.shippingSaved$.subscribe(() => this.markShippingConfigurationSaved());
  }

  /** A Free plan is never billed, so there's nothing to put a card on file for. Same fix as
   *  business-setup-checklist.component.ts's isFreePlan: businessSetup[0].subscription.planId is a
   *  cosmosId string, not the local catalog's small 1-4 id, so it must be matched against the
   *  catalog rather than compared/parsed directly. */
  get isFreePlan(): boolean {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const cosmosId = setup?.subscription?.planId ?? setup?.subscription?.PlanId;
    if (!cosmosId) return false;
    const matched = this.planDataService.list.find((p: any) => String(p.cosmosId) === String(cosmosId));
    return !!matched && (Number(matched.id) === 1 || String(matched.planName || '').trim().toLowerCase() === 'free');
  }

  /** All three localStorage fallback markers below (sales channels, domain/template, shipping)
   *  must be scoped per user+business, same convention as auth.guard.ts's
   *  getStoreSetupV2CompletedKey() — a flat, unscoped key means once ANY account in this browser
   *  completes a step once, every other account tested afterward (even a brand-new one that never
   *  touched it) reads as "Complete" too, confirmed live: a fresh account showed Domain & store
   *  template as Complete while its own businessSetup record still had onlineStoreCreated:false
   *  and onlineStoreUrl:"". */
  private scopedStorageKey(prefix: string): string {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const businessId = String(setup?.businessId ?? setup?.id ?? 'default-business');
    const userId = String(GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId ?? 'default-user');
    return `${prefix}-${userId}-${businessId}`;
  }

  get hasSavedSalesChannels(): boolean {
    return localStorage.getItem(this.scopedStorageKey(this.salesChannelsStorageKey)) === '1';
  }

  markSalesChannelsSaved(): void {
    localStorage.setItem(this.scopedStorageKey(this.salesChannelsStorageKey), '1');
    this.invalidate();
  }

  get hasSavedDomainTemplate(): boolean {
    return localStorage.getItem(this.scopedStorageKey(this.domainTemplateStorageKey)) === '1';
  }

  markDomainTemplateSaved(): void {
    localStorage.setItem(this.scopedStorageKey(this.domainTemplateStorageKey), '1');
    this.invalidate();
  }

  /** Local-optimistic fallback for a submitted custom-online-store request — same convention as
   *  markDomainTemplateSaved(), used until a user refetch echoes back the backend's persisted
   *  CustomOnlineStoreRequested flag. */
  get hasRequestedCustomOnlineStore(): boolean {
    return localStorage.getItem(this.scopedStorageKey(this.customOnlineStoreRequestedStorageKey)) === '1';
  }

  markCustomOnlineStoreRequested(): void {
    localStorage.setItem(this.scopedStorageKey(this.customOnlineStoreRequestedStorageKey), '1');
    this.invalidate();
  }

  /** Fallback marker for "has the merchant actually saved a shipping zone" — see
   *  isShippingConfigurationSaved's doc comment for why the backend response alone (zones[0]
   *  present + isEnabled) can't be trusted here. Set by ShippingConfigrationComponent.saveFn()
   *  (V1) and ShippingRulesV2Component.save() (V2) on a successful save, same convention as
   *  markDomainTemplateSaved()/markSalesChannelsSaved(). */
  get hasSavedShippingConfiguration(): boolean {
    return localStorage.getItem(this.scopedStorageKey(this.shippingConfigurationStorageKey)) === '1';
  }

  markShippingConfigurationSaved(): void {
    localStorage.setItem(this.scopedStorageKey(this.shippingConfigurationStorageKey), '1');
    this.invalidate();
  }

  /** Canonical "is a billing card on file" check — real backend state (StripeCardService's
   *  attachPaymentMethod() persists CardBrand/CardLast4/CardExpMonth/CardExpYear onto
   *  BusinessSetup.Subscription.TransactionDetails), not a localStorage flag, so it survives a
   *  fresh login on any device. Presence of a last4 is the signal — there's no separate boolean
   *  field persisted alongside it (only echoed back in the attach response). */
  get hasCardOnFile(): boolean {
    const transactionDetails = GlobalService.userInfo?.businessSetup?.[0]?.subscription?.transactionDetails;
    return !!(transactionDetails?.cardLast4 ?? transactionDetails?.CardLast4);
  }

  /** Local-patch the in-memory user record right after StripeAddCardComponent confirms a card is
   *  on file, so hasCardOnFile (and every consumer of it — the checklist step, the trial-lockout
   *  overlay) reflects the save immediately without waiting on a full user refetch. The backend
   *  already persisted this via attachPaymentMethod(); this just keeps the client copy in sync. */
  applyCardOnFile(event: StripeCardSavedEvent): void {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    if (!setup) return;
    setup.subscription = setup.subscription || {};
    setup.subscription.transactionDetails = {
      ...(setup.subscription.transactionDetails || {}),
      cardBrand: event.cardBrand,
      cardLast4: event.cardLast4,
      cardExpMonth: event.cardExpMonth,
      cardExpYear: event.cardExpYear
    };
    this.invalidate();
  }

  /** Canonical "is Domain & store template actually saved" check — the one every consumer
   *  (checklist page, topbar badge, welcome modal, theme-overview's "visit site" button) should
   *  use instead of reimplementing it. onlineStoreCreated is the real backend field: it defaults
   *  to false at signup and only flips once this step is actually saved (confirmed via live
   *  account data), so unlike hasSavedDomainTemplate alone it survives a fresh login or a
   *  different browser/device — hasSavedDomainTemplate is kept only as a fallback for accounts
   *  saved before this field existed. Deliberately NOT combined with onlineStoreInitiated (only
   *  means the merchant opened the old store-setup wizard, not that a store exists — see
   *  notifyOnlineStoreInitiated() in business-setup.service.ts) or onlineStoreUrl (a save that
   *  stamped the flag but failed to write the URL would wrongly read as incomplete otherwise). */
  /** Canonical "is the Business information step actually complete" check — mirrors
   *  business-setup-checklist.component.ts's stricter businessInfoSaved (name, type, description,
   *  street/city/state/zip all present), not just the looser address/city/state/zip check used
   *  for the overall setup-progress count. Needed because a merchant can have a real street
   *  address saved while business type/description are still blank, which the loose check alone
   *  would wrongly treat as complete — used by the topbar to gate showing the business name. */
  get isBusinessInfoComplete(): boolean {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    if (!setup) return false;

    const zip = String(setup.zip ?? setup.zipCode ?? setup.Zip ?? '').trim();
    // "00000" is the placeholder ZIP sentinel signup-v6 writes when business info was never
    // actually filled in (see business-setup-checklist.component.ts's isPlaceholderBusinessInfo) —
    // never treat it as a real, saved value.
    if (!zip || zip === '00000') return false;

    const businessName = String(setup.businessDisplayName ?? setup.businessName ?? setup.BusinessName ?? '').trim();
    const address = String(setup.address ?? setup.Address ?? '').trim();
    const city = String(setup.city ?? setup.City ?? '').trim();
    const state = String(setup.state ?? setup.State ?? '').trim();

    const industryRaw = String(setup.industry ?? setup.Industry ?? '').trim();
    const [industryName, description] = industryRaw.includes(' || ')
      ? industryRaw.split(' || ').map((part: string) => part.trim())
      : [industryRaw, ''];

    return !!(businessName && address && city && state && industryName && description);
  }

  get isDomainTemplateSaved(): boolean {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const onlineStoreCreated = setup?.onlineStoreCreated ?? setup?.OnlineStoreCreated;
    const customOnlineStoreRequested =
      setup?.customOnlineStoreRequested ?? setup?.CustomOnlineStoreRequested
      ?? setup?.availSaveOffer ?? setup?.AvailSaveOffer;
    return !!onlineStoreCreated || !!customOnlineStoreRequested || this.hasSavedDomainTemplate || this.hasRequestedCustomOnlineStore;
  }

  invalidate(): void {
    this.cachedProgress$ = null;
    this.refreshingSubject.next(true);
    this.getProgress(true).subscribe({
      next: () => this.refreshingSubject.next(false),
      error: () => this.refreshingSubject.next(false)
    });
  }

  getProgress(forceRefresh: boolean = false): Observable<BusinessSetupProgress> {
    if (!forceRefresh && this.cachedProgress$) {
      return this.cachedProgress$;
    }

    const setup = GlobalService.userInfo?.businessSetup?.[0];
    // signup-v6's "Where do you want to sell?" screen is mandatory and writes the real answer
    // into HasPOS/StandardOnlineStore at account creation (see account-information.component.ts's
    // saveBusinessInformation()), so these genuinely reflect a merchant choice for v6 signups —
    // trust them, falling back to the explicit checklist save for older accounts that predate that.
    const hasPOS = setup?.hasPOS ?? setup?.HasPOS;
    const hasPOSLite = setup?.hasPOSLite ?? setup?.HasPOSLite;
    const standardOnlineStore = setup?.standardOnlineStore ?? setup?.StandardOnlineStore;
    const channelsComplete = this.hasSavedSalesChannels || !!(hasPOS || hasPOSLite || standardOnlineStore);
    // Industry "0" is the unset default signup writes, so it means business info was never saved.
    const industrySaved = !!String(setup?.industry ?? setup?.Industry ?? '').trim()
      && String(setup?.industry ?? setup?.Industry ?? '').trim() !== '0';
    // A Pakistani business is not asked for a postal code, so it must not be required here either
    // (the checklist's business step drops it for PK too — the two must agree or the badge and page differ).
    const zipOk = !zipIsRequired(countryOfBusiness(setup, GlobalService.countryCode)) || !!(setup?.zip || setup?.zipCode);
    const businessComplete = !!(setup?.address && setup?.city && setup?.state && zipOk) && industrySaved;

    // Domain & store template / Shipping only apply to merchants selling online — same signal as
    // business-setup-checklist.component.ts's domainStepNeeded/shippingStepNeeded. Keeping this in
    // sync matters: this service also drives the topbar setup-progress badge, which was showing a
    // stale "X of 6" even after the checklist itself correctly dropped to "X of 4" for in-store-only
    // accounts.
    const domainStepNeeded = !!standardOnlineStore;
    const shippingStepNeeded = !!standardOnlineStore;

    const productsRequest = this.productsService
      .getAllProductList4InventoryV3({ PageNo: 0, PageSize: 1 }, null, true)
      .pipe(
        map((res: any) => !!(res?.success && Array.isArray(res?.data) && res.data.length > 0)),
        catchError(() => of(false))
      );

    // getWebStoreConfiguration() always returns a template/webStoreHost for every account (an
    // auto-provisioned default, unrelated to any merchant action), so it can't be used as a
    // completion signal — see isDomainTemplateSaved's own doc comment for why onlineStoreCreated
    // alone (not OR'd with onlineStoreInitiated/onlineStoreUrl) is the right field.
    const hasTemplate = this.isDomainTemplateSaved;

    // isEnabled is unconditionally true on every zone the UI ever writes (V2's newZone() defaults
    // it to true) — and also true on whatever the backend auto-provisions before the merchant ever
    // opens shipping settings, per a live account showing this step marked complete on a signup
    // that never touched it. isSaved is only ever stamped at the moment of a real save
    // (shipping-configuration.component.ts's saveFn(), shipping-rules-v2.component.ts's
    // prepareShippingData()), same convention getPaymentProcessing() already uses correctly
    // (data.isSaved) — OR'd with hasSavedShippingConfiguration as a fallback in case the backend
    // doesn't actually persist/echo the per-zone flag back on GET.
    const shippingRequest = this.businessSetupService.getShippingConfiguration(true).pipe(
      map((res: any) => {
        const zones = res?.data?.zones;
        const zoneSaved = !!(res?.success && Array.isArray(zones) && zones.length > 0 && zones[0]?.isSaved === true);
        return zoneSaved || this.hasSavedShippingConfiguration;
      }),
      catchError(() => of(this.hasSavedShippingConfiguration))
    );

    const paymentRequest = this.businessSetupService.getPaymentProcessing(true).pipe(
      map((res: any) => !!(res?.success && res?.data?.isSaved === true)),
      catchError(() => of(false))
    );

    // paymentconfig's isSaved flag isn't stamped by Stripe Connect onboarding, so a merchant who
    // finished Connect (chargesEnabled) but never separately saved paymentconfig would otherwise
    // stay stuck below 100% here forever while the checklist page itself already shows "Connected"
    // (business-setup-checklist.component.ts's checkStripeAccountStatus() uses this same signal).
    const stripeConnectRequest = this.businessSetupService.CheckConnectAccount().pipe(
      map((res: any) => !!(res?.success && res?.data?.chargesEnabled)),
      catchError(() => of(false))
    );

    this.cachedProgress$ = forkJoin({
      hasProducts: productsRequest,
      hasShipping: shippingRequest,
      hasPayment: paymentRequest,
      hasStripeConnect: stripeConnectRequest
    }).pipe(
      map(({ hasProducts, hasShipping, hasPayment, hasStripeConnect }) => {
        const domainComplete = !domainStepNeeded || hasTemplate;
        const shippingComplete = !shippingStepNeeded || hasShipping;
        // Backend-computed at signup from country/IP (Stripe isn't available in Pakistan, e.g.);
        // undefined (accounts predating this flag) must default to "step needed" so nothing
        // changes for them — see business-setup-checklist.component.ts's stripeStepNeeded.
        const setup = GlobalService.userInfo?.businessSetup?.[0] as any;
        // Connect Stripe is offered everywhere except Pakistan, where the step is hidden (and so not counted).
        const stripeStepNeeded = countryOfBusiness(setup, GlobalService.countryCode) !== 'PK';
        const stripeComplete = !stripeStepNeeded || hasPayment || hasStripeConnect;
        const cardStepNeeded = !this.isFreePlan;
        const cardComplete = !cardStepNeeded || this.hasCardOnFile;
        // Base step count is 7 — Domain & store template / Shipping drop for an in-store-only
        // merchant, Connect payment processing drops when the backend flagged the merchant as
        // unable to use Stripe, and Add card drops for a Free plan (never billed, so no card
        // needed).
        const totalCount = 7 - (domainStepNeeded ? 0 : 1) - (shippingStepNeeded ? 0 : 1)
          - (stripeStepNeeded ? 0 : 1) - (cardStepNeeded ? 0 : 1);
        const completedCount = [
          channelsComplete,
          businessComplete,
          ...(domainStepNeeded ? [domainComplete] : []),
          hasProducts,
          ...(shippingStepNeeded ? [shippingComplete] : []),
          ...(stripeStepNeeded ? [stripeComplete] : []),
          ...(cardStepNeeded ? [cardComplete] : [])
        ].filter(Boolean).length;

        return {
          channelsComplete,
          businessComplete,
          domainComplete,
          productsComplete: hasProducts,
          shippingComplete,
          stripeComplete,
          cardComplete,
          completedCount,
          totalCount,
          sellsOnline: domainStepNeeded,
          cardStepNeeded,
          stripeStepNeeded
        };
      }),
      tap((progress) => this.progressSubject.next(progress)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.cachedProgress$;
  }
}
